import { useEffect, useState, useContext, useCallback, memo, useMemo, useLayoutEffect, useRef } from "react";
import moment from "moment";
import debounce from "debounce";
import { FixedSizeList, areEqual } from "react-window";

//Auth0
import { useAuth0, OAuthError } from "@auth0/auth0-react";

//Context
import AppContext from "../../context/appContext";

//db
import * as localDb from "../../services/localDb";
import { LocalDbAuthenticationError } from "../../services/localDb";

//Components
import Box from "@mui/material/Box";
import ListItem from "@mui/material/ListItem";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import { TextField, useMediaQuery } from "@mui/material";

//Icons
import PlayCircleOutlined from "@mui/icons-material/PlayCircleOutlineOutlined";
import CircleIcon from "@mui/icons-material/Circle";

//Services
import refDataById from "../../services/localRefData/refDataById";
import statusColorCoder from "../../services/helpers/statusColorCoder.js";
import { ruutsApi } from "../../services/ruutsApi";
import EventOverview from "../EventOverview/EventOverview";
import { appStore } from "../../services/lfStorage/lfStorage";

import { SyncIndicator } from "./SyncIndicator";
import { Search } from "@mui/icons-material";
import { DeleteIndicator } from "./DeleteIndicator.jsx";
import isNullOrUndefined from "../../services/helpers/isNullOrUndefined.js";
import { TOOLBAR_HEIGHT } from "../../constants/layout.js";

const Row = memo(({ data, index, style }) => {
  const { events, farms, showProgramTag, getProgramTag, handleListItemClick, handleUpload } = data;
  const eventData = events[index];
  const farm = farms?.find((farm) => farm.id === eventData?.farmId);
  return (
    <ListItem
      divider
      key={index}
      sx={{ ...style }}
    >
      <Grid
        container
        width={"100%"}
      >
        <Grid
          item
          xs={9}
          sm={9}
        >
          <Stack
            overflow={"hidden"}
            spacing={1}
            direction={"column"}
            height={"100%"}
            display="flex"
            alignItems={"flex-start"}
            justifyContent={"space-between"}
          >
            <Box
              display="flex"
              width={"auto"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              {eventData?.tags instanceof Map && eventData.tags.get("nuevo") && (
                <CircleIcon
                  ml={0.5}
                  color="primary"
                  sx={{ fontSize: "12px" }}
                />
              )}
              <Typography
                sx={{
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  ml: 0.5,
                }}
                component="div"
                variant="h6"
              >
                {eventData ? eventData.name.toUpperCase() : ""} ({farm ? farm.province : ""})
              </Typography>
            </Box>
            <Stack pl={0.5}>
              <Typography
                componet="div"
                variant="body"
                color={statusColorCoder(eventData?.taskStatusId)}
              >
                {refDataById("taskStatus", eventData?.taskStatusId).toLowerCase()}
              </Typography>
              <Typography variant="body2"> {moment.utc(eventData?.date).format("YYYY-MM-DD")}</Typography>
            </Stack>
            {!eventData?.isDeleted ? (
              <SyncIndicator
                eventData={eventData}
                handleUpload={handleUpload}
              />
            ) : (
              <DeleteIndicator eventData={eventData} />
            )}
          </Stack>
        </Grid>
        <Grid
          item
          xs={3}
          sm={3}
        >
          <Stack
            overflow={"hidden"}
            width={"100%"}
            direction={"column"}
            height={"100%"}
            display="flex"
            alignItems={"flex-end"}
            justifyContent={"space-between"}
          >
            <Stack
              spacing={1}
              direction={"row"}
            >
              {showProgramTag && isNullOrUndefined(farm?.programId) ? getProgramTag(99) : getProgramTag(farm?.programId)}
            </Stack>
            <IconButton
              sx={{ padding: 0, margin: 0 }}
              color="primary"
              onClick={() => handleListItemClick(eventData, farm)}
            >
              <PlayCircleOutlined sx={{ fontSize: "50px" }} />
            </IconButton>
          </Stack>
        </Grid>
      </Grid>
    </ListItem>
  );
}, areEqual);

export default function MyEvents() {
  const {
    setShowFilters,
    currentEvent,
    setCurrentEvent,
    setLoadingSomething,
    isOnline,
    setAlert,
    setNotify,
    setCurrentUser,
    currentUser,
    storageFreeze,
    storageData,
    windowWidth,
    windowHeight,
    appBarHeight,
  } = useContext(AppContext);
  const showProgramTag = useMediaQuery("(min-width:300px)");
  const [myEvents, setEvents] = useState();
  const [farms, setFarms] = useState();
  const [searchString, setSearchString] = useState(null);
  const [currentFarm, setCurrentFarm] = useState();
  const [modalStatus, setModalStatus] = useState(false);
  const [listHeight, setListHeight] = useState(windowHeight || 0);
  const searchBoxRef = useRef(null);
  const syncInProgressRef = useRef(false);
  const { getAccessTokenSilently, loginWithRedirect, error, isLoading, user, isAuthenticated } = useAuth0();

  const searchEvents = useCallback(
    (eventData) => {
      if (searchString && searchString.length) {
        return eventData?.name?.toLowerCase().includes(searchString.toLowerCase());
      }
      return true;
    },
    [searchString]
  );

  const handleSearchInput = debounce((value) => setSearchString(value), 300);

  const loginFlow = useCallback(async (explicitLogin = false) => {
    // Si es login explícito y el usuario no está autenticado, intentar login directamente
    if (explicitLogin && !isAuthenticated) {
      if (!window.navigator.onLine) {
        throw new Error("No hay conexión a internet. Por favor, verifique su conectividad e intente nuevamente.");
      }
      // Si es login explícito y no está autenticado, intentar hacer login directamente
      await loginWithRedirect();
      return; // loginWithRedirect redirige la página, así que no continuamos
    }

    try {
      await getAccessTokenSilently({ detailedResponse: true });
    } catch (e) {
      console.error(e);
      // Preservar currentUser incluso si Auth0 falla temporalmente
      // Esto permite acceso offline a datos locales
      if (currentUser) {
        console.log("Preservando currentUser para acceso offline:", currentUser.userName);
        // Asegurar que currentUser se mantiene en appStore
        try {
          await appStore.set("currentUser", currentUser);
        } catch (storeError) {
          console.warn("No se pudo preservar currentUser en appStore:", storeError);
        }
      }

      // Si es un login explícito (desde botón), siempre intentar hacer login si hay conexión
      if (explicitLogin) {
        if (!window.navigator.onLine) {
          throw new Error("No hay conexión a internet. Por favor, verifique su conectividad e intente nuevamente.");
        }
        // Si es login explícito, intentar hacer login siempre
        await loginWithRedirect();
      } else {
        // Comportamiento original: solo hacer login si es OAuthError y hay conexión
        if (e instanceof OAuthError && window.navigator.onLine) {
          await loginWithRedirect();
        }
      }
    }
  }, [loginWithRedirect, getAccessTokenSilently, currentUser, isAuthenticated]);

  const getLocalEvents = useCallback(async () => {
    try {
      // Validar consistencia antes de acceder a los datos
      const storedUser = await appStore.get("currentUser");

      // Si hay currentUser en el contexto pero no en appStore, intentar sincronizar
      if (currentUser && !storedUser) {
        console.warn("currentUser en contexto pero no en appStore, sincronizando...");
        try {
          await appStore.set("currentUser", currentUser);
        } catch (e) {
          console.error("Error al sincronizar currentUser a appStore:", e);
        }
      }

      // Si hay storedUser pero no coincide con currentUser del contexto, validar
      if (storedUser && currentUser && storedUser.userName !== currentUser.userName) {
        console.warn(`Inconsistencia detectada: storedUser (${storedUser.userName}) != currentUser (${currentUser.userName})`);
        // Intentar usar el storedUser si podemos acceder a la base de datos
        try {
          await localDb.getDb();
          // Si podemos abrir la DB, actualizar el contexto con storedUser
          setCurrentUser(storedUser);
        } catch (e) {
          // Si no podemos, usar el currentUser del contexto
          console.log("Usando currentUser del contexto");
        }
      }

      let events = await localDb.getMany("events").then((result) => result.toArray());
      let farms = await localDb.getMany("farms").then((result) => result.toArray());
      setFarms(farms);
      setEvents(events);
    } catch (error) {
      if (error instanceof LocalDbAuthenticationError) {
        console.warn("LocalDb: usuario no disponible, esperando autenticación...");
      } else {
        console.error("Error al obtener eventos locales:", error);
        setNotify({
          message: `Error al cargar eventos locales: ${error.message}`,
          severity: "error",
          show: true,
        });
      }
    }
  }, [currentUser, setCurrentUser, setNotify]);

  const toggleModal = useCallback(
    ({ reload }) => {
      setModalStatus((prev) => !prev);
      if (reload) {
        getLocalEvents();
      }
    },
    [getLocalEvents]
  );

  const handleListItemClick = useCallback(
    async (eventData, farmData) => {
      try {
        setCurrentEvent(eventData);
        setCurrentFarm(farmData);
        if (eventData.tags && eventData.tags instanceof Map) {
          eventData.tags.set("nuevo", false);
          await localDb.update("events", eventData.id, { tags: eventData.tags }, false);
        }
      } catch (e) {
        console.error("Unable to set tags:", e.message);
      } finally {
        toggleModal({ reload: false });
      }
    },
    [setCurrentEvent, setCurrentFarm, toggleModal]
  );

  const handleUpload = useCallback(
    async (changes, eventData, element) => {
      try {
        if (!isAuthenticated) {
          return setAlert({
            message: "Necesita iniciar sesión para sincornizar datos al servidor. Asegúrese de tener conectividad. ¿Desea continuar?",
            severity: "warning",
            show: true,
            actionButtonTitle: "CONTINUAR",
            action: async () => {
              try {
                // Intentar login explícito
                await loginFlow(true);
                // Si loginFlow no lanzó error y no redirigió, significa que getAccessTokenSilently fue exitoso
                // En ese caso, el usuario ya está autenticado y podemos sincronizar
                // Nota: Si loginWithRedirect() se ejecutó, la página se redirigirá y este código no se ejecutará
                // Cuando el usuario vuelva después del login, deberá intentar sincronizar nuevamente
                await ruutsApi.syncEventDataUp(changes, eventData.id, element);
              } catch (loginError) {
                console.error("Error en login:", loginError);
                // Si el error es sobre falta de conexión, mostrar mensaje específico
                if (loginError.message && loginError.message.includes("conexión")) {
                  setNotify({
                    message: loginError.message,
                    severity: "error",
                    show: true,
                  });
                } else if (!loginError.message || !loginError.message.includes("redirect")) {
                  // No mostrar error si fue un redirect (eso es normal)
                  setNotify({
                    message: loginError.message || "Error al iniciar sesión. Por favor, verifique su conexión e intente nuevamente.",
                    severity: "error",
                    show: true,
                  });
                }
                throw loginError;
              }
            },
          });
        }
        await ruutsApi.syncEventDataUp(changes, eventData.id, element);
      } catch (e) {
        console.error(e);
        setNotify({
          message: `Error al sincronizar los datos al servidor. Si el error persiste, contacte al administrador. Mensage: ${e.message}`,
          severity: "error",
          show: true,
        });
      }
    },
    [isAuthenticated, setAlert, loginFlow, setNotify]
  );

  const getProgramTag = useCallback((programId) => {
    if (programId === 99) return null;
    let name = refDataById("programs", programId);
    if (name)
      return (
        <Chip
          variant="outlined"
          color="secondary"
          label={name}
          size="small"
        />
      );
    return null;
  }, []);

  useEffect(() => {
    async function setLocalUser() {
      try {
        // Validar que el usuario tiene userName antes de guardarlo
        if (!user || !user.userName) {
          console.warn("Usuario de Auth0 no tiene userName válido");
          return;
        }

        // Verificar si hay un currentUser existente y validar consistencia
        const existingUser = await appStore.get("currentUser");

        if (existingUser && existingUser.userName && existingUser.userName !== user.userName) {
          console.warn(`Desincronización detectada: currentUser (${existingUser.userName}) != Auth0 user (${user.userName})`);
          // Intentar validar cuál es el correcto verificando bases de datos Dexie
          try {
            await localDb.getDb();
            // Si podemos abrir la base de datos con el userName existente, mantenerlo
            // Esto previene pérdida de datos si el usuario cambió en Auth0 pero los datos locales son del anterior
            console.log(`Manteniendo currentUser existente (${existingUser.userName}) para preservar datos locales`);
            setCurrentUser(existingUser);
            return;
          } catch (e) {
            // Si no se puede abrir con el userName existente, usar el nuevo de Auth0
            console.log(`Actualizando currentUser a nuevo userName de Auth0: ${user.userName}`);
          }
        }

        // Guardar el usuario en appStore
        await appStore.set("currentUser", user);
        setCurrentUser(user);
      } catch (e) {
        console.error("Error al establecer usuario local:", e);
        // Si falla guardar en appStore pero tenemos user de Auth0, al menos actualizar el estado
        if (user) {
          setCurrentUser(user);
        }
      }
    }
    if (!isLoading && !error && user) {
      setLocalUser();
    }
  }, [user, isLoading, error, setCurrentUser]);

  useEffect(() => {
    async function checkAuth() {
      if (!isOnline || isAuthenticated) return;
      let currentUser = await appStore.get("currentUser");
      if (currentUser) {
        setAlert({
          message: "Necesita iniciar sesión para poder descargar eventos nuevos. Asegúrese de tener conectividad. ¿Desea iniciar sesión ahora?",
          severity: "warning",
          show: true,
          actionButtonTitle: "Iniciar sesión",
          cancelButtonTitle: "Seguir trabajando",
          action: async () => {
            try {
              await loginFlow(true);
            } catch (loginError) {
              console.error("Error en login:", loginError);
              if (loginError.message && !loginError.message.includes("redirect")) {
                setNotify({
                  message: loginError.message || "Error al iniciar sesión. Por favor, verifique su conexión e intente nuevamente.",
                  severity: "error",
                  show: true,
                });
              }
              throw loginError;
            }
          },
          cancelAction: () => { },
        });
      } else {
        console.log("user never authenticated");
        loginFlow();
      }
    }
    if (!isLoading && !error && !storageFreeze) {
      checkAuth();
    }
  }, [isOnline, isAuthenticated, isLoading, error, storageFreeze, loginFlow]);

  useEffect(() => {
    async function syncData() {
      if (!isOnline || !isAuthenticated || !currentUser) return;
      if (syncInProgressRef.current) return;
      syncInProgressRef.current = true;
      try {
        setLoadingSomething(true);
        await ruutsApi.downloadMonitoringData(user);
        await ruutsApi.getReferenceData();
        let events = await localDb.getMany("events").then((result) => result.toArray());
        let farms = await localDb.getMany("farms").then((result) => result.toArray());
        setFarms(farms);
        setEvents(events);
      } catch (e) {
        setNotify({
          message: `Error al descargar datos. Si el error persiste, contacte al administrador (${e.message})`,
          severity: "error",
          show: true,
        });
      } finally {
        syncInProgressRef.current = false;
        setLoadingSomething(false);
      }
    }
    if (!isLoading && !error && !storageFreeze) {
      syncData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, isAuthenticated, user?.sub, user?.["/hubId"], user?.name, storageFreeze, isLoading, error, currentUser]);

  useEffect(() => {
    if (storageFreeze) {
      if (storageData.colorCode === "warning")
        setAlert({
          severity: "warning",
          show: true,
          message: `El espacio utilizado es de ${storageData.usagePct} %. Se recomienda liberar espacio en su dispositivo.`,
        });
      if (storageData.colorCode === "error")
        setAlert({
          show: true,
          severity: "error",
          message: `El espacio utilizado es de ${storageData.usagePct} %. Se encuentra por encima del lìmite recomendado. Libere espacio en su dispositivo para seguir trabajando. No borre los datos del navegador a menos que haya sincornizado todos los eventos con el servidor.`,
        });
    }
  }, [storageData, storageFreeze, setAlert]);


  useEffect(() => {
    getLocalEvents();
    setShowFilters(false);
  }, [setShowFilters, currentUser, getLocalEvents]);

  useEffect(() => {
    return () => handleSearchInput.clear();
  }, [handleSearchInput]);

  useLayoutEffect(() => {
    const searchHeight = searchBoxRef.current?.offsetHeight || TOOLBAR_HEIGHT;
    const nextHeight = Math.max(0, (windowHeight || 0) - searchHeight - (appBarHeight || 0));
    setListHeight((prev) => (prev !== nextHeight ? nextHeight : prev));
  }, [windowHeight, appBarHeight]);

  const filteredEvents = useMemo(() => {
    if (!myEvents) {
      return [];
    }
    return myEvents.filter(searchEvents);
  }, [myEvents, searchEvents]);

  const rowData = useMemo(
    () => ({
      events: filteredEvents,
      farms,
      showProgramTag,
      getProgramTag,
      handleListItemClick,
      handleUpload,
    }),
    [filteredEvents, farms, showProgramTag, getProgramTag, handleListItemClick, handleUpload]
  );

  return (
    myEvents &&
    farms && (
      <Box className="event-list">
        <Box ref={searchBoxRef}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Buscar por nombre del establecimiento"
            InputProps={{
              type: "search",
              endAdornment: <Search color="primary" />,
            }}
            onChange={(e) => handleSearchInput(e.target.value)}
          />
        </Box>
        <FixedSizeList
          height={listHeight}
          width={windowWidth}
          itemCount={filteredEvents.length}
          overscanCount={1}
          itemSize={140}
          itemData={rowData}
          itemKey={(index, data) => data.events[index].id}
        >
          {Row}
        </FixedSizeList>
        {currentEvent && (
          <EventOverview
            handleClose={toggleModal}
            eventData={currentEvent}
            farm={currentFarm}
            show={modalStatus}
          />
        )}
      </Box>
    )
  );
}
